import { platformService } from './platformService';
import { jobTypeService } from './jobTypeService';
import { jobGroupService } from './jobGroupService';
import { authService } from '../features/auth/services/authService';
import { ssotService } from './ssotService';
import { yamlConfigService } from './yamlConfigService';
import { apiEndpointService } from './apiEndpointService';
import { extractorService } from './extractorService';
import { loaderService } from './loaderService';
import { apiCache, CACHE_CONFIG } from './cache/apiCache';

export interface PreloadData {
  // Phase 1: Core Reference Data
  platforms: any[];
  jobTypes: any[];
  groups: any[];
  userInfo: any;
  
  // Phase 2: Configuration Context
  schemas: any[];
  yamlConfigs: any[];
  apiEndpoints: any[];
  extractors: any[];
  loaders: any[];
  
  // Additional data for specific tabs (future extensibility)
  jobGroups?: any[];
  etlJobs?: any[];
}

export interface PreloadOptions {
  // Future extensibility for additional optional data
  includeJobGroups?: boolean;
  includeEtlJobs?: boolean;
  // Control which configuration data to load
  includeLoaders?: boolean;
  includeExtractors?: boolean;
  includeYamlConfigs?: boolean;
  includeApiEndpoints?: boolean;
}

class PreloadService {
  private cache: PreloadData | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours (much longer since we'll clear on logout)
  private readonly CACHE_KEY = 'tesseract-preload-cache';
  private readonly CACHE_TIMESTAMP_KEY = 'tesseract-preload-timestamp';

  // Load cache from localStorage
  private loadCacheFromStorage(): void {
    try {
      const cachedData = localStorage.getItem(this.CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
      
      if (cachedData && cachedTimestamp) {
        this.cache = JSON.parse(cachedData);
        this.cacheTimestamp = parseInt(cachedTimestamp);
      }
    } catch (error) {
      console.warn('Failed to load cache from localStorage:', error);
      this.clearCache();
    }
  }

  // Save cache to localStorage
  private saveCacheToStorage(): void {
    try {
      if (this.cache) {
        localStorage.setItem(this.CACHE_KEY, JSON.stringify(this.cache));
        localStorage.setItem(this.CACHE_TIMESTAMP_KEY, this.cacheTimestamp.toString());
      }
    } catch (error) {
      console.warn('Failed to save cache to localStorage:', error);
    }
  }

  async preloadEssentials(options: PreloadOptions = {}): Promise<PreloadData> {
    try {
      // Load cache from localStorage if not already loaded
      if (!this.cache) {
        this.loadCacheFromStorage();
      }

      // Check cache first
      if (this.cache && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION) {
        return this.cache;
      }

      console.log('Preloading essential data...');

      // Phase 1: Core Reference Data (essential for all operations)
      const phase1Promise = Promise.allSettled([
        platformService.getAllPlatforms(),
        jobTypeService.getAllJobTypes(),
        jobGroupService.getAllJobGroups(),
        authService.getUserInfo()
      ]);

      // Phase 2: Configuration Context (conditionally loaded based on options)
      const phase2Promises: Promise<any>[] = [
        ssotService.getAllSsotSchemas() // Always load schemas as they're essential
      ];
      
      // Conditionally add other services
      if (options.includeYamlConfigs !== false) {
        phase2Promises.push(yamlConfigService.getAllYamlConfigs());
      }
      if (options.includeApiEndpoints !== false) {
        phase2Promises.push(apiEndpointService.getApiEndpoints());
      }
      if (options.includeExtractors !== false) {
        phase2Promises.push(extractorService.getAllExtractors());
      }
      if (options.includeLoaders !== false) {
        phase2Promises.push(loaderService.getAllLoaders());
      }
      
      const phase2Promise = Promise.allSettled(phase2Promises);

      // Execute phases in parallel
      const [phase1Results, phase2Results] = await Promise.all([
        phase1Promise,
        phase2Promise
      ]);

      // Extract successful results from Phase 1
      const [platformsResult, jobTypesResult, groupsResult, userInfoResult] = phase1Results;
      
      const platforms = platformsResult.status === 'fulfilled' ? platformsResult.value : [];
      const jobTypes = jobTypesResult.status === 'fulfilled' ? jobTypesResult.value : [];
      const groups = groupsResult.status === 'fulfilled' ? groupsResult.value : [];
      const userInfo = userInfoResult.status === 'fulfilled' ? userInfoResult.value : null;

      // Extract successful results from Phase 2 (handle variable number of results)
      let resultIndex = 0;
      const schemas = phase2Results[resultIndex++].status === 'fulfilled' 
        ? (phase2Results[0] as PromiseFulfilledResult<any>).value 
        : [];
      
      const yamlConfigs = (options.includeYamlConfigs !== false && phase2Results[resultIndex]) 
        ? (phase2Results[resultIndex].status === 'fulfilled' 
           ? (phase2Results[resultIndex++] as PromiseFulfilledResult<any>).value 
           : (resultIndex++, []))
        : [];
        
      const apiEndpoints = (options.includeApiEndpoints !== false && phase2Results[resultIndex])
        ? (phase2Results[resultIndex].status === 'fulfilled' 
           ? (phase2Results[resultIndex++] as PromiseFulfilledResult<any>).value 
           : (resultIndex++, []))
        : [];
        
      const extractors = (options.includeExtractors !== false && phase2Results[resultIndex])
        ? (phase2Results[resultIndex].status === 'fulfilled' 
           ? (phase2Results[resultIndex++] as PromiseFulfilledResult<any>).value 
           : (resultIndex++, []))
        : [];
        
      const loaders = (options.includeLoaders !== false && phase2Results[resultIndex])
        ? (phase2Results[resultIndex].status === 'fulfilled' 
           ? (phase2Results[resultIndex++] as PromiseFulfilledResult<any>).value 
           : (resultIndex++, []))
        : [];

      // Base preload data
      const preloadData: PreloadData = {
        platforms,
        jobTypes,
        groups,
        userInfo,
        schemas,
        yamlConfigs,
        apiEndpoints,
        extractors,
        loaders
      };

      // Optional additional data based on requirements (future extensibility)
      // Currently no additional optional data needed

      // Cache the results
      this.cache = preloadData;
      this.cacheTimestamp = Date.now();
      
      // Save to localStorage for persistence across page refreshes
      this.saveCacheToStorage();

      console.log('Preload completed:', {
        platforms: platforms.length,
        jobTypes: jobTypes.length,
        groups: groups.length,
        userInfo: !!userInfo,
        schemas: schemas.length,
        yamlConfigs: yamlConfigs.length,
        apiEndpoints: apiEndpoints.length,
        extractors: extractors.length,
        loaders: loaders.length
      });

      return preloadData;
    } catch (error) {
      console.error('Preload failed:', error);
      throw error;
    }
  }

  // Smart data exchange helpers
  getAvailableJobTypes(sourcePlatformId: number, targetPlatformId: number): any[] {
    if (!this.cache) return [];
    
    return this.cache.jobTypes.filter((jt: any) =>
      jt.source_platform_id === sourcePlatformId &&
      jt.target_platform_id === targetPlatformId
    );
  }

  getJobTypesByPlatform(platformId: number, isSource: boolean = true): any[] {
    if (!this.cache) return [];
    
    const field = isSource ? 'source_platform_id' : 'target_platform_id';
    return this.cache.jobTypes.filter((jt: any) => jt[field] === platformId);
  }

  getExistingSchema(jobTypeId: number): any | null {
    if (!this.cache) return null;
    
    // First try to get schemas from the job type itself
    const jobType = this.cache.jobTypes.find((jt: any) => jt.id === jobTypeId);
    if (jobType?.schemas) {
      // Handle both string and array formats from different API versions
      let schemaId: number;
      if (typeof jobType.schemas === 'string') {
        // Mock server format: schemas is a string that might contain ID
        const parsedId = parseInt(jobType.schemas);
        if (!isNaN(parsedId)) {
          schemaId = parsedId;
        } else {
          // If string doesn't contain valid ID, fallback to job_type field
          return this.cache.schemas.find((s: any) => s.job_type === jobTypeId && s.is_active);
        }
      } else if (Array.isArray(jobType.schemas) && jobType.schemas.length > 0) {
        // Production format: schemas is an array
        schemaId = typeof jobType.schemas[0] === 'string' ? parseInt(jobType.schemas[0]) : jobType.schemas[0];
      } else {
        // Fallback to job_type field
        return this.cache.schemas.find((s: any) => s.job_type === jobTypeId && s.is_active);
      }
      
      return this.cache.schemas.find((s: any) => s.id === schemaId && s.is_active) || null;
    }
    
    // Fallback to filtering by job_type field
    return this.cache.schemas.find((s: any) => 
      s.job_type === jobTypeId && s.is_active
    );
  }

  getExistingSchemas(jobTypeId: number): any[] {
    if (!this.cache) return [];
    
    // First try to get schemas from the job type itself
    const jobType = this.cache.jobTypes.find((jt: any) => jt.id === jobTypeId);
    if (jobType?.schemas) {
      // Handle both string and array formats from different API versions
      let schemaIds: number[] = [];
      
      if (typeof jobType.schemas === 'string') {
        // Mock server format: schemas is a string that might contain ID
        const parsedId = parseInt(jobType.schemas);
        if (!isNaN(parsedId)) {
          schemaIds = [parsedId];
        }
      } else if (Array.isArray(jobType.schemas) && jobType.schemas.length > 0) {
        // Production format: schemas is an array
        schemaIds = jobType.schemas.map((s: any) => typeof s === 'string' ? parseInt(s) : s);
      }
      
      if (schemaIds.length > 0) {
        return this.cache.schemas.filter((s: any) => 
          schemaIds.includes(s.id) && s.is_active
        );
      }
    }
    
    // Fallback to filtering by job_type field
    return this.cache.schemas.filter((s: any) => 
      s.job_type === jobTypeId && s.is_active
    );
  }

  getEndpointsByPlatform(platformId: number): any[] {
    if (!this.cache) return [];
    
    return this.cache.apiEndpoints.filter((ep: any) => ep.platform_id === platformId);
  }

  getPlatformById(platformId: number): any | null {
    if (!this.cache) return null;
    
    return this.cache.platforms.find((p: any) => p.id === platformId);
  }

  getJobTypeById(jobTypeId: number): any | null {
    if (!this.cache) return null;
    
    return this.cache.jobTypes.find((jt: any) => jt.id === jobTypeId);
  }

  // Validation helpers
  validatePlatformJobTypeRelationship(sourcePlatformId: number, targetPlatformId: number, jobTypeId: number): boolean {
    const jobType = this.getJobTypeById(jobTypeId);
    if (!jobType) return false;
    
    return jobType.source_platform_id === sourcePlatformId && 
           jobType.target_platform_id === targetPlatformId;
  }

  // Cache management
  clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
    
    // Clear localStorage as well
    try {
      localStorage.removeItem(this.CACHE_KEY);
      localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
    } catch (error) {
      console.warn('Failed to clear cache from localStorage:', error);
    }
    
    // Also clear API cache
    apiCache.clearAll();
  }

  // Clear API cache for specific data types
  clearApiCache(dataType?: 'platforms' | 'jobTypes' | 'jobs' | 'all'): void {
    if (dataType === 'all' || !dataType) {
      apiCache.clearAll();
    } else {
      // Clear specific patterns - this would need pattern matching in cache
      apiCache.clearAll(); // For now, clear all
    }
  }

  isCacheValid(): boolean {
    return this.cache !== null && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
  }

  getCachedData(): PreloadData | null {
    return this.isCacheValid() ? this.cache : null;
  }

  // Refresh specific data types
  async refreshPlatforms(): Promise<any[]> {
    try {
      const platforms = await platformService.getAllPlatforms();
      if (this.cache) {
        this.cache.platforms = platforms;
      }
      return platforms;
    } catch (error) {
      console.error('Failed to refresh platforms:', error);
      return [];
    }
  }

  async refreshJobTypes(): Promise<any[]> {
    try {
      const jobTypes = await jobTypeService.getAllJobTypes();
      if (this.cache) {
        this.cache.jobTypes = jobTypes;
      }
      return jobTypes;
    } catch (error) {
      console.error('Failed to refresh job types:', error);
      return [];
    }
  }

  async refreshSchemas(): Promise<any[]> {
    try {
      const schemas = await ssotService.getAllSsotSchemas();
      if (this.cache) {
        this.cache.schemas = schemas;
      }
      return schemas;
    } catch (error) {
      console.error('Failed to refresh schemas:', error);
      return [];
    }
  }
}

export const preloadService = new PreloadService();